import React, { createContext, useContext, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import * as Sentry from "@sentry/react";
import {
  login as apiLogin,
  logout as apiLogout,
  getCurrentUser,
} from "../api/auth";
import { User } from "../types/auth";
import { setAuthState } from "../utils/api";
import { isLoginPath, loginPathFor } from "../utils/loginRedirect";
import { DisclaimerModal } from "../components/DisclaimerModal";

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  updateUser: (updates: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const updateUser = (updates: Partial<User>) => {
    setUser((prev) => (prev ? { ...prev, ...updates } : prev));
  };

  // Public story links (/s/<slug>) are viewable without a session — never
  // bounce an anonymous visitor from them to the login page.
  const onPublicRoute = location.pathname.startsWith("/s/");

  // Signed out: go to the login page, remembering where we were. Already on
  // a login page: stay, so ?next= and ?sso_error= survive.
  const redirectToLogin = () => {
    if (onPublicRoute || isLoginPath(location.pathname)) return;
    navigate(loginPathFor(location.pathname, location.search, location.hash));
  };

  const checkAuth = async () => {
    setLoading(true);
    try {
      const currentUser = await getCurrentUser();
      if (currentUser) {
        setUser(currentUser);
        // Check if user needs to accept disclaimer
        const disclaimerAccepted = localStorage.getItem('disclaimerAccepted') === 'true';
        if (!disclaimerAccepted) {
          setShowDisclaimer(true);
        } else {
          // Signed in already (e.g. back from single sign-on, or a stale tab
          // on the login page): honour ?next=, else go to projects. On any
          // other route stay put.
          if (location.pathname === "/" || isLoginPath(location.pathname)) {
            completeLogin();
          }
        }
      } else {
        setUser(null);
        redirectToLogin();
      }
    } catch (error) {
      setUser(null);
      redirectToLogin();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []); // Only run on mount

  // Attach the current user's identity to Sentry so errors, performance
  // traces and session replays are tied to a known user. Cleared on logout.
  useEffect(() => {
    if (user) {
      Sentry.setUser({
        id: String(user.id),
        username: user.username,
        email: user.email,
      });
    } else {
      Sentry.setUser(null);
    }
  }, [user]);

  // Register auth state with API utility for session expiration handling
  // This allows the API utility to automatically handle session expiration
  // by calling logout and redirecting to login page
  useEffect(() => {
    const handleLogout = () => {
      setUser(null);
    };
    
    setAuthState(handleLogout, navigate);
  }, [navigate]);

  // Where to go after a successful login. Honours ?next= on the login URL —
  // used by the MCP OAuth consent flow, which sends unauthenticated browsers
  // here and needs them back at the API's /oauth/authorize afterwards.
  // Guarded against open redirects: relative SPA paths, or absolute URLs on
  // this host / a subdomain sharing this host as suffix (the API origin,
  // e.g. api-internal.mitigate.<zone> vs UI mitigate.<zone>).
  const resolveNextUrl = (): string | null => {
    const next = new URLSearchParams(location.search).get("next");
    if (!next) return null;
    if (next.startsWith("/") && !next.startsWith("//")) return next;
    try {
      const url = new URL(next);
      const isLocal =
        url.hostname === "localhost" || url.hostname === "127.0.0.1";
      const sameSite =
        url.hostname === window.location.hostname ||
        url.hostname.endsWith("." + window.location.hostname) ||
        (isLocal && ["localhost", "127.0.0.1"].includes(window.location.hostname));
      const schemeOk =
        url.protocol === "https:" || (url.protocol === "http:" && isLocal);
      if (sameSite && schemeOk) return next;
    } catch {
      // Malformed URL — fall through to the default.
    }
    return null;
  };

  const completeLogin = () => {
    const next = resolveNextUrl();
    if (next === null) {
      navigate("/projects");
    } else if (next.startsWith("/")) {
      navigate(next);
    } else {
      // Cross-origin (the API host) — a full page navigation is required.
      window.location.href = next;
    }
  };

  const login = async (username: string, password: string) => {
    try {
      const userData = await apiLogin(username, password);
      setUser(userData);

      // Check if user needs to accept disclaimer
      const disclaimerAccepted = localStorage.getItem('disclaimerAccepted') === 'true';
      if (!disclaimerAccepted) {
        setShowDisclaimer(true);
      } else {
        completeLogin();
      }
    } catch (error) {
      throw error;
    }
  };

  const handleDisclaimerAccept = () => {
    localStorage.setItem('disclaimerAccepted', 'true');
    setShowDisclaimer(false);
    completeLogin();
  };

  const logout = async () => {
    try {
      await apiLogout();
      setUser(null);
      // Clear disclaimer acceptance on logout
      localStorage.removeItem('disclaimerAccepted');
      setShowDisclaimer(false);
      navigate("/login");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  return (
    <AuthContext.Provider
      value={{ user, loading, login, logout, checkAuth, updateUser }}
    >
      {children}
      <DisclaimerModal 
        open={showDisclaimer} 
        onAccept={handleDisclaimerAccept} 
      />
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
